import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './PianoContiAnaliticaPage.api';
import './PianoContiAnaliticaPage.css';

const MODULO = 'controllo_gestione.piano_conti_analitica';

type Natura = 'COSTO' | 'RICAVO';

interface ContoNode {
  id: number;
  societaId: number;
  parentId: number | null;
  divisioneId: number | null;
  livello: number;
  codice: string;
  descrizione: string;
  natura: Natura;
  attivo: boolean;
  figli: ContoNode[];
}

interface Divisione {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  colore: string | null;
  attivo: boolean;
}

const LIVELLO_LABEL: Record<number, string> = { 1: 'Centro', 2: 'Conto', 3: 'Sottoconto' };

// Etichetta del "figlio" che si può creare sotto un nodo di un certo livello
const FIGLIO_LABEL: Record<number, string> = { 1: 'Conto', 2: 'Sottoconto' };

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

interface ModalContoState {
  mode: 'create' | 'edit';
  parent: ContoNode | null;          // create: padre (null = nuovo Centro)
  node: ContoNode | null;            // edit: nodo in modifica
  livello: number;
  presetDivisioneId?: number;        // se si clicca "+ Nuovo Centro" dentro una divisione
}

interface FormContoState {
  descrizione: string;
  natura: Natura;
  societaId: string;
  divisioneId: string;
}

interface ModalDivisioneState {
  mode: 'create' | 'edit';
  record: Divisione | null;
}

interface FormDivisioneState {
  codice: string;
  descrizione: string;
  societaId: string;
}

export default function PianoContiAnaliticaPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const {
    societaId,
    setSocietaId,
    listaSocieta,
    isUnica,
  } = useFiltroSocieta();

  const [tree, setTree] = useState<ContoNode[]>([]);
  const [divisioni, setDivisioni] = useState<Divisione[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);
  const [expandedConti, setExpandedConti] = useState<Set<number>>(new Set());
  const [expandedDivisioni, setExpandedDivisioni] = useState<Set<number>>(new Set());

  const [modalConto, setModalConto] = useState<ModalContoState | null>(null);
  const [formConto, setFormConto] = useState<FormContoState>({ descrizione: '', natura: 'COSTO', societaId: '', divisioneId: '' });
  const [formContoError, setFormContoError] = useState('');
  const [savingConto, setSavingConto] = useState(false);

  const [modalDivisione, setModalDivisione] = useState<ModalDivisioneState | null>(null);
  const [formDivisione, setFormDivisione] = useState<FormDivisioneState>({ codice: '', descrizione: '', societaId: '' });
  const [formDivisioneError, setFormDivisioneError] = useState('');
  const [savingDivisione, setSavingDivisione] = useState(false);

  const collectExpandableConti = (nodes: ContoNode[], acc: Set<number>) => {
    for (const n of nodes) {
      if (n.figli.length) { acc.add(n.id); collectExpandableConti(n.figli, acc); }
    }
  };

  const load = async () => {
    if (!societaId) { setTree([]); setDivisioni([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/conti-analitici?societaId= -> { success: true, data: [{ id, societaId, parentId, divisioneId, livello, codice, descrizione, natura, attivo, figli }] }
      const resConti = await apiFetch<{ success: boolean; data: ContoNode[] }>(
        `/api/conti-analitici?societaId=${societaId}`,
      );
      // @api GET /api/divisioni-analitiche?societaId= -> { success: true, data: [{ id, societaId, codice, descrizione, colore, attivo }] }
      const resDiv = await apiFetch<{ success: boolean; data: Divisione[] }>(
        `/api/divisioni-analitiche?societaId=${societaId}`,
      );
      const dataTree = resConti.data ?? [];
      const dataDiv = resDiv.data ?? [];
      setTree(dataTree);
      setDivisioni(dataDiv);
      const exp = new Set<number>();
      collectExpandableConti(dataTree, exp);
      setExpandedConti(exp);
      setExpandedDivisioni(new Set(dataDiv.map(d => d.id)));
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  // ── Raggruppamento Centri per divisione ───────────────────────────────────
  const centriByDivisione = useMemo(() => {
    const m = new Map<number | 'orphans', ContoNode[]>();
    for (const c of tree) {
      const k = c.divisioneId ?? 'orphans';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(c);
    }
    return m;
  }, [tree]);

  // ── Toggle espansione ─────────────────────────────────────────────────────
  const toggleConto = (id: number) => {
    setExpandedConti(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleDivisione = (id: number) => {
    setExpandedDivisioni(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const espandiTutto = () => {
    const exp = new Set<number>();
    collectExpandableConti(tree, exp);
    setExpandedConti(exp);
    setExpandedDivisioni(new Set(divisioni.map(d => d.id)));
  };
  const comprimiTutto = () => {
    setExpandedConti(new Set());
    setExpandedDivisioni(new Set());
  };

  // ── Modale Divisione ──────────────────────────────────────────────────────
  const openCreateDivisione = () => {
    setModalDivisione({ mode: 'create', record: null });
    setFormDivisione({ codice: '', descrizione: '', societaId: societaId || '' });
    setFormDivisioneError('');
  };
  const openEditDivisione = (d: Divisione) => {
    setModalDivisione({ mode: 'edit', record: d });
    setFormDivisione({ codice: d.codice, descrizione: d.descrizione, societaId: String(d.societaId) });
    setFormDivisioneError('');
  };
  const closeModalDivisione = () => { setModalDivisione(null); setFormDivisioneError(''); };

  const salvaDivisione = async () => {
    if (!modalDivisione) return;
    const codice = formDivisione.codice.trim();
    const descrizione = formDivisione.descrizione.trim();
    if (!codice) { setFormDivisioneError('Codice obbligatorio'); return; }
    if (!descrizione) { setFormDivisioneError('Descrizione obbligatoria'); return; }

    setSavingDivisione(true);
    setFormDivisioneError('');
    try {
      if (modalDivisione.mode === 'create') {
        const sid = formDivisione.societaId ? Number(formDivisione.societaId) : 0;
        if (!sid) { setFormDivisioneError('Seleziona una società'); setSavingDivisione(false); return; }
        // @api POST /api/divisioni-analitiche { societaId, codice, descrizione } -> { success: true, data: { id } }
        await apiFetch('/api/divisioni-analitiche', {
          method: 'POST',
          body: JSON.stringify({ societaId: sid, codice, descrizione }),
        });
      } else if (modalDivisione.record) {
        // @api PUT /api/divisioni-analitiche/:id { codice, descrizione, attivo } -> { success: true, data: { id } }
        await apiFetch(`/api/divisioni-analitiche/${modalDivisione.record.id}`, {
          method: 'PUT',
          body: JSON.stringify({ codice, descrizione }),
        });
      }
      closeModalDivisione();
      load();
    } catch (e: any) {
      setFormDivisioneError(e.message || 'Errore nel salvataggio');
    } finally {
      setSavingDivisione(false);
    }
  };

  const toggleDivisioneAttivo = async (d: Divisione) => {
    try {
      await apiFetch(`/api/divisioni-analitiche/${d.id}`, {
        method: 'PUT',
        body: JSON.stringify({ attivo: !d.attivo }),
      });
      load();
    } catch (e: any) {
      setError(e.message || 'Errore nel cambio stato');
    }
  };

  const eliminaDivisione = async (d: Divisione) => {
    if (!window.confirm(`Eliminare la Divisione ${d.codice} — ${d.descrizione}?`)) return;
    try {
      // @api DELETE /api/divisioni-analitiche/:id -> { success: true }
      await apiFetch(`/api/divisioni-analitiche/${d.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      window.alert(e.message || 'Errore nell\'eliminazione');
    }
  };

  // ── Modale Conto (Centro/Conto/Sottoconto) ────────────────────────────────
  const divisioniAttive = useMemo(() => divisioni.filter(d => d.attivo), [divisioni]);

  const openCreateCentro = (presetDivisioneId?: number) => {
    setModalConto({ mode: 'create', parent: null, node: null, livello: 1, presetDivisioneId });
    setFormConto({
      descrizione: '',
      natura: 'COSTO',
      societaId: societaId || '',
      divisioneId: presetDivisioneId ? String(presetDivisioneId) : (divisioniAttive[0]?.id ? String(divisioniAttive[0].id) : ''),
    });
    setFormContoError('');
  };

  const openCreateFiglio = (parent: ContoNode) => {
    setModalConto({ mode: 'create', parent, node: null, livello: parent.livello + 1 });
    setFormConto({
      descrizione: '',
      natura: parent.natura,
      societaId: String(parent.societaId),
      divisioneId: '',
    });
    setFormContoError('');
  };

  const openEditConto = (node: ContoNode) => {
    setModalConto({ mode: 'edit', parent: null, node, livello: node.livello });
    setFormConto({
      descrizione: node.descrizione,
      natura: node.natura,
      societaId: String(node.societaId),
      divisioneId: node.divisioneId ? String(node.divisioneId) : '',
    });
    setFormContoError('');
  };

  const closeModalConto = () => { setModalConto(null); setFormContoError(''); };

  const salvaConto = async () => {
    if (!modalConto) return;
    const descrizione = formConto.descrizione.trim();
    if (!descrizione) { setFormContoError('Descrizione obbligatoria'); return; }

    setSavingConto(true);
    setFormContoError('');
    try {
      if (modalConto.mode === 'create') {
        if (modalConto.livello === 1) {
          const sid = formConto.societaId ? Number(formConto.societaId) : 0;
          if (!sid) { setFormContoError('Seleziona una società'); setSavingConto(false); return; }
          if (!formConto.divisioneId) { setFormContoError('Seleziona una Divisione'); setSavingConto(false); return; }
          // @api POST /api/conti-analitici { societaId, parentId, divisioneId, descrizione, natura } -> { success: true, data: { id, codice, livello, natura } }
          await apiFetch('/api/conti-analitici', {
            method: 'POST',
            body: JSON.stringify({
              societaId: sid,
              parentId: null,
              divisioneId: Number(formConto.divisioneId),
              descrizione,
              natura: formConto.natura,
            }),
          });
        } else {
          await apiFetch('/api/conti-analitici', {
            method: 'POST',
            body: JSON.stringify({
              societaId: modalConto.parent!.societaId,
              parentId: modalConto.parent!.id,
              descrizione,
            }),
          });
        }
      } else if (modalConto.node) {
        const payload: Record<string, unknown> = { descrizione };
        if (modalConto.node.livello === 1) {
          payload.natura = formConto.natura;
          if (formConto.divisioneId) payload.divisioneId = Number(formConto.divisioneId);
        }
        // @api PUT /api/conti-analitici/:id { descrizione, natura, divisioneId, attivo } -> { success: true, data: { id } }
        await apiFetch(`/api/conti-analitici/${modalConto.node.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      closeModalConto();
      load();
    } catch (e: any) {
      setFormContoError(e.message || 'Errore nel salvataggio');
    } finally {
      setSavingConto(false);
    }
  };

  const toggleAttivoConto = async (node: ContoNode) => {
    try {
      await apiFetch(`/api/conti-analitici/${node.id}`, {
        method: 'PUT',
        body: JSON.stringify({ attivo: !node.attivo }),
      });
      load();
    } catch (e: any) {
      setError(e.message || 'Errore nel cambio stato');
    }
  };

  const eliminaConto = async (node: ContoNode) => {
    if (!window.confirm(`Eliminare ${LIVELLO_LABEL[node.livello]} ${node.codice} — ${node.descrizione}?`)) return;
    try {
      // @api DELETE /api/conti-analitici/:id -> { success: true }
      await apiFetch(`/api/conti-analitici/${node.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      window.alert(e.message || 'Errore nell\'eliminazione');
    }
  };

  // ── Render ricorsivo righe Conti ──────────────────────────────────────────
  const renderRowsConti = (nodes: ContoNode[]): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    for (const n of nodes) {
      if (!mostraDisattivi && !n.attivo) continue;
      const hasFigli = n.figli.length > 0;
      const isOpen = expandedConti.has(n.id);
      out.push(
        <tr key={`c-${n.id}`} className={n.attivo ? '' : 'pca-row--inactive'}>
          <td>
            <div className={`pca-codice-cell pca-codice-cell--liv${n.livello}`}>
              {hasFigli ? (
                <button
                  type="button"
                  className={`pca-toggle ${isOpen ? 'pca-toggle--open' : ''}`}
                  onClick={() => toggleConto(n.id)}
                  aria-label={isOpen ? 'Comprimi' : 'Espandi'}
                >
                  <RowIcon.Chevron />
                </button>
              ) : (
                <span className="pca-toggle-spacer" />
              )}
              <span className="pca-codice">{n.codice}</span>
            </div>
          </td>
          <td>{n.descrizione}</td>
          <td><span className="pca-livello-tag">{LIVELLO_LABEL[n.livello]}</span></td>
          <td>
            <span className={`bop-badge ${n.natura === 'RICAVO' ? 'bop-badge--success' : 'bop-badge--warning'}`}>
              {n.natura === 'RICAVO' ? 'Ricavo' : 'Costo'}
            </span>
          </td>
          <td>
            <span className={`bop-badge ${n.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
              {n.attivo ? 'Attivo' : 'Disattivo'}
            </span>
          </td>
          <td className="bop-text-right">
            <BopRowActions
              actions={[
                {
                  icon: <PlusIcon />,
                  label: `Aggiungi ${FIGLIO_LABEL[n.livello]}`,
                  onClick: () => openCreateFiglio(n),
                  variant: 'primary',
                  hidden: !canEdit || n.livello >= 3,
                },
                {
                  icon: <RowIcon.Edit />,
                  label: 'Modifica',
                  onClick: () => openEditConto(n),
                  hidden: !canEdit,
                },
                {
                  icon: <RowIcon.Disable />,
                  label: n.attivo ? 'Disattiva' : 'Riattiva',
                  onClick: () => toggleAttivoConto(n),
                  hidden: !canEdit,
                },
                {
                  icon: <RowIcon.Trash />,
                  label: 'Elimina',
                  onClick: () => eliminaConto(n),
                  variant: 'danger',
                  hidden: !canDelete,
                },
              ]}
            />
          </td>
        </tr>,
      );
      if (hasFigli && isOpen) out.push(...renderRowsConti(n.figli));
    }
    return out;
  };

  // ── Render righe per Divisione (intestazione + centri al suo interno) ─────
  const renderRowsDivisione = (d: Divisione, centri: ContoNode[]): React.ReactNode[] => {
    const isOpen = expandedDivisioni.has(d.id);
    const centriVisibili = mostraDisattivi ? centri : centri.filter(c => c.attivo);
    const numCentri = centriVisibili.length;
    const rows: React.ReactNode[] = [];

    rows.push(
      <tr key={`d-${d.id}`} className={`pca-row--divisione ${d.attivo ? '' : 'pca-row--inactive'}`}>
        <td>
          <div className="pca-codice-cell pca-codice-cell--liv0">
            <button
              type="button"
              className={`pca-toggle ${isOpen ? 'pca-toggle--open' : ''}`}
              onClick={() => toggleDivisione(d.id)}
              aria-label={isOpen ? 'Comprimi divisione' : 'Espandi divisione'}
            >
              <RowIcon.Chevron />
            </button>
            <span className="pca-codice pca-codice--divisione">{d.codice}</span>
          </div>
        </td>
        <td>
          <strong>{d.descrizione}</strong>{' '}
          <span className="bop-text-muted">({numCentri} centro/i)</span>
        </td>
        <td><span className="pca-livello-tag pca-livello-tag--divisione">Divisione</span></td>
        <td>—</td>
        <td>
          <span className={`bop-badge ${d.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
            {d.attivo ? 'Attiva' : 'Disattiva'}
          </span>
        </td>
        <td className="bop-text-right">
          <BopRowActions
            actions={[
              {
                icon: <PlusIcon />,
                label: 'Aggiungi Centro',
                onClick: () => openCreateCentro(d.id),
                variant: 'primary',
                hidden: !canEdit || !d.attivo,
              },
              {
                icon: <RowIcon.Edit />,
                label: 'Modifica',
                onClick: () => openEditDivisione(d),
                hidden: !canEdit,
              },
              {
                icon: <RowIcon.Disable />,
                label: d.attivo ? 'Disattiva' : 'Riattiva',
                onClick: () => toggleDivisioneAttivo(d),
                hidden: !canEdit,
              },
              {
                icon: <RowIcon.Trash />,
                label: 'Elimina',
                onClick: () => eliminaDivisione(d),
                variant: 'danger',
                hidden: !canDelete,
              },
            ]}
          />
        </td>
      </tr>,
    );

    if (isOpen) {
      if (centriVisibili.length === 0) {
        rows.push(
          <tr key={`d-${d.id}-empty`} className="pca-row--empty">
            <td colSpan={6}>
              <span className="bop-text-muted">Nessun Centro in questa divisione.</span>
            </td>
          </tr>,
        );
      } else {
        rows.push(...renderRowsConti(centriVisibili));
      }
    }
    return rows;
  };

  // Centri senza divisione (incoerenza dati / migrazione incompleta)
  const orphans = (centriByDivisione.get('orphans') ?? []) as ContoNode[];

  const renderOrphansBlock = (): React.ReactNode[] => {
    if (orphans.length === 0) return [];
    const orphansVisibili = mostraDisattivi ? orphans : orphans.filter(c => c.attivo);
    if (orphansVisibili.length === 0) return [];
    return [
      <tr key="d-orphans" className="pca-row--divisione pca-row--orphans">
        <td colSpan={6}>
          <strong>Centri senza divisione</strong>{' '}
          <span className="bop-text-muted">({orphansVisibili.length}) — assegna una divisione tramite "Modifica"</span>
        </td>
      </tr>,
      ...renderRowsConti(orphansVisibili),
    ];
  };

  const noSocieta = !societaId;

  const rows: React.ReactNode[] = [];
  for (const d of divisioni) {
    const centri = (centriByDivisione.get(d.id) ?? []) as ContoNode[];
    rows.push(...renderRowsDivisione(d, centri));
  }
  rows.push(...renderOrphansBlock());

  return (
    <Layout>
      <div className="piano-conti-analitica">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Piano dei conti analitica</h2>
            <p>Struttura a quattro livelli: Divisione → Centro → Conto → Sottoconto</p>
          </div>
          {canEdit && (
            <div className="bop-flex">
              <button className="bop-btn bop-btn-secondary" onClick={openCreateDivisione} disabled={noSocieta}>
                + Nuova Divisione
              </button>
              <button
                className="bop-btn bop-btn-primary"
                onClick={() => openCreateCentro()}
                disabled={noSocieta || divisioniAttive.length === 0}
                title={divisioniAttive.length === 0 ? 'Crea prima almeno una Divisione' : undefined}
              >
                + Nuovo Centro
              </button>
            </div>
          )}
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <label className="bop-flex">
            <input type="checkbox" checked={mostraDisattivi} onChange={e => setMostraDisattivi(e.target.checked)} />
            <span>Mostra anche disattivi</span>
          </label>
          {!noSocieta && (divisioni.length > 0 || tree.length > 0) && (
            <div className="pca-spacer">
              <button className="bop-btn bop-btn-secondary" onClick={espandiTutto}>Espandi tutto</button>
              <button className="bop-btn bop-btn-secondary" onClick={comprimiTutto}>Comprimi tutto</button>
            </div>
          )}
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per gestire il suo piano dei conti.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : divisioni.length === 0 && tree.length === 0 ? (
              <div className="bop-empty-state">
                Nessuna divisione o centro presente.{canEdit ? ' Inizia creando una Divisione.' : ''}
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Livello</th>
                    <th>Natura</th>
                    <th>Stato</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>{rows}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modale Divisione */}
      {modalDivisione && (
        <Modal
          title={modalDivisione.mode === 'edit'
            ? `Modifica Divisione ${modalDivisione.record?.codice ?? ''}`.trim()
            : 'Nuova Divisione'}
          onClose={closeModalDivisione}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModalDivisione}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaDivisione} disabled={savingDivisione}>
                {savingDivisione ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formDivisioneError && <div className="bop-inline-error">{formDivisioneError}</div>}

          {modalDivisione.mode === 'create' && !isUnica && (
            <div className="bop-form-group">
              <label className="bop-label">Società *</label>
              <select
                className="bop-select"
                value={formDivisione.societaId}
                onChange={e => setFormDivisione(f => ({ ...f, societaId: e.target.value }))}
              >
                <option value="">Seleziona…</option>
                {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Codice *</label>
              <input
                className="bop-input"
                placeholder="es. D01, MKT…"
                value={formDivisione.codice}
                onChange={e => setFormDivisione(f => ({ ...f, codice: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Descrizione *</label>
              <input
                className="bop-input"
                placeholder="es. Direzione Marketing"
                value={formDivisione.descrizione}
                onChange={e => setFormDivisione(f => ({ ...f, descrizione: e.target.value }))}
              />
            </div>
          </div>

          <p className="pca-hint">La Divisione raggruppa Centri di costo e ricavo per consentire confronti per area.</p>
        </Modal>
      )}

      {/* Modale Conto */}
      {modalConto && (
        <Modal
          title={
            modalConto.mode === 'edit'
              ? `Modifica ${LIVELLO_LABEL[modalConto.livello]} ${modalConto.node?.codice ?? ''}`.trim()
              : modalConto.livello === 1
                ? 'Nuovo Centro'
                : `Nuovo ${LIVELLO_LABEL[modalConto.livello]} sotto ${modalConto.parent?.codice ?? ''}`
          }
          onClose={closeModalConto}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModalConto}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaConto} disabled={savingConto}>
                {savingConto ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formContoError && <div className="bop-inline-error">{formContoError}</div>}

          {modalConto.mode === 'create' && modalConto.livello === 1 && !isUnica && (
            <div className="bop-form-group">
              <label className="bop-label">Società *</label>
              <select
                className="bop-select"
                value={formConto.societaId}
                onChange={e => setFormConto(f => ({ ...f, societaId: e.target.value }))}
              >
                <option value="">Seleziona…</option>
                {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}

          {modalConto.livello === 1 && (
            <div className="bop-form-group">
              <label className="bop-label">Divisione *</label>
              <select
                className="bop-select"
                value={formConto.divisioneId}
                onChange={e => setFormConto(f => ({ ...f, divisioneId: e.target.value }))}
              >
                <option value="">Seleziona divisione…</option>
                {divisioniAttive.map(d => (
                  <option key={d.id} value={d.id}>{d.codice} — {d.descrizione}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              placeholder={modalConto.livello === 1 ? 'es. Produzione, Commerciale…' : 'es. Materie prime, Stipendi…'}
              value={formConto.descrizione}
              onChange={e => setFormConto(f => ({ ...f, descrizione: e.target.value }))}
              autoFocus
            />
          </div>

          {modalConto.livello === 1 ? (
            <div className="bop-form-group">
              <label className="bop-label">Natura *</label>
              <select
                className="bop-select"
                value={formConto.natura}
                onChange={e => setFormConto(f => ({ ...f, natura: e.target.value as Natura }))}
              >
                <option value="COSTO">Costo</option>
                <option value="RICAVO">Ricavo</option>
              </select>
              {modalConto.mode === 'edit' && (
                <p className="pca-hint">La modifica della natura sarà applicata anche a tutti i Conti e Sottoconti collegati.</p>
              )}
            </div>
          ) : (
            <div className="bop-form-group">
              <label className="bop-label">Natura</label>
              <p className="pca-hint">
                Ereditata dal Centro:{' '}
                <span className={`bop-badge ${formConto.natura === 'RICAVO' ? 'bop-badge--success' : 'bop-badge--warning'}`}>
                  {formConto.natura === 'RICAVO' ? 'Ricavo' : 'Costo'}
                </span>
              </p>
            </div>
          )}

          <p className="pca-hint">Il codice viene assegnato automaticamente alla creazione.</p>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
